import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import { LocaleProvider, useLocale } from './context/LocaleContext';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Navbar from './components/Navbar';
import DocumentTitle from './components/DocumentTitle';
import ScrollToTop from './components/ScrollToTop';
import LocaleHead from './components/LocaleHead';
import './components/Layout.css';
import Home from './pages/Home';
import Articles from './pages/Articles';
import ArticleDetail from './pages/ArticleDetail';
import Services from './pages/Services';
import ServiceLanding from './pages/ServiceLanding';
import ProjectDetail from './pages/ProjectDetail';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutFailed from './pages/CheckoutFailed';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import UserPanel from './pages/UserPanel';
import AdminPanel from './pages/AdminPanel';
import { getProtectedRouteDecision } from './utils/authSession';
import './App.css';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, authLoading } = useAuth();
  const { localePath } = useLocale();
  const decision = getProtectedRouteDecision({
    authLoading,
    isAuthenticated,
    user,
    requireAdmin,
  });

  if (decision === 'loading') {
    return (
      <main className="route-auth-loading" role="status" aria-live="polite">
        <span aria-hidden="true" />
        <p>{t('common.restoringAccount')}</p>
      </main>
    );
  }

  if (decision === 'login') {
    return <Navigate to={localePath('/login')} replace />;
  }

  if (decision === 'user-panel') {
    return <Navigate to={localePath('/user-panel')} replace />;
  }

  return children;
};

const LocaleBoundary = () => {
  const { locale: routeLocale } = useParams();
  const { locales, locale, localesLoading } = useLocale();
  const location = useLocation();
  const isSupported = locales.some((item) => item.code === routeLocale && item.enabled !== false);

  if (localesLoading) return null;
  if (!isSupported) {
    const suffix = location.pathname === '/' ? '' : location.pathname;
    return <Navigate to={`/${locale.code}${suffix}${location.search}`} replace />;
  }
  return <Outlet />;
};

const RootRedirect = () => {
  const { locales, locale, localesLoading } = useLocale();
  if (localesLoading) return null;
  const saved = localStorage.getItem('haryad-locale');
  const destination = locales.some((item) => item.code === saved && item.enabled !== false)
    ? saved
    : locale.code;
  return <Navigate to={`/${destination}`} replace />;
};

const AdminShell = () => (
  <div className="layout">
    <Navbar />
    <main className="main-content admin-main">
      <AdminPanel />
    </main>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <Router>
        <LocaleProvider>
          <SiteSettingsProvider>
            <DocumentTitle />
            <LocaleHead />
            <ScrollToTop />
            <Routes>
              <Route
                path="/admin-panel/*"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminShell />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<RootRedirect />} />
              <Route path="/:locale" element={<LocaleBoundary />}>
                <Route path="login" element={<Login />} />
                <Route path="register" element={<Register />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="reset-password/:token" element={<ResetPassword />} />
                <Route element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="articles" element={<Articles />} />
                  <Route path="articles/:id" element={<ArticleDetail />} />
                  <Route path="services" element={<Services />} />
                  <Route path="services/:slug/:landingSlug" element={<ServiceLanding />} />
                  <Route path="services/:slug" element={<Services />} />
                  <Route path="projects/:type/:id" element={<ProjectDetail />} />
                  <Route path="projects/:id" element={<ProjectDetail />} />
                  <Route path="products" element={<Products />} />
                  <Route path="products/:id" element={<ProductDetail />} />
                  <Route path="checkout/:productId" element={<Checkout />} />
                  <Route path="checkout/success" element={<CheckoutSuccess />} />
                  <Route path="checkout/failed" element={<CheckoutFailed />} />
                  <Route path="about" element={<About />} />
                  <Route path="contact" element={<Contact />} />
                  <Route
                    path="user-panel/*"
                    element={
                      <ProtectedRoute>
                        <UserPanel />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>
              <Route path="*" element={<LocaleBoundary />} />
            </Routes>
          </SiteSettingsProvider>
        </LocaleProvider>
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
